import { useState, useEffect } from "react";
import { DateTime } from "luxon";

import Button from "core/components/Button";
import Typography from "core/components/Typography";
import useAuthenticatedMutation from "core/hooks/useAuthenticatedMutation";

import {
  generateOrgDataAccessCredential,
  generateUserDataAccessCredential,
  disableIndividualCredentialById,
  disableOrganizationCredentialById,
} from "../actions";
import DownloadNewCredentialModal from "./DownloadNewCredentialModal";
import Error from "core/components/Error";
import { downloadJson } from "./util";
import ButtonLink from "core/components/ButtonLink";
import { Row } from "core/styles";
import { ROLES } from "core/constants";
import useCurrentUser from "core/hooks/useCurrentUser";
import { getLastActiveCredential } from "../utilities";
import DetailsViewer from "../DetailsViewer";
import { useParams } from "react-router";
import ConfirmModal from "core/components/ConfirmModal";
import useActiveMembership from "core/hooks/useActiveMembership";
import Accordion, {
  AccordionContent,
  AccordionItem,
} from "core/components/Accordion";
import Pill from "core/components/Pill";
import { dh_colors } from "theme";
import { toast } from "sonner";

const CREDENTIAL_PERMISSIONS = {
  CAN_DOWNLOAD: "user-credentials:download",
  CAN_DISABLE: "user-credentials:disable",
  CAN_VIEW_META: "user-credentials:view-meta",
};

// Matt: I think we need to decompose this and consider a different pattern
const credSettings = {
  user: {
    text: `Download your individual credential for BQ.
        Individual credentials are valid for 30 days, at which point you will
        have to download new credentials.`,
    getDownloadInfo: (generatedTimeStamp) => {
      return generatedTimeStamp
        ? `${DateTime.fromMillis(generatedTimeStamp).toFormat("D")}`
        : "";
    },
    getExpiryInfo: (daysToExpire) => {
      if (daysToExpire === 0) {
        return "Expires Today";
      } else if (daysToExpire < 0) {
        return `Expired on ${DateTime.now()
          .plus({ days: daysToExpire })
          .toFormat("D")}`;
      } else if (daysToExpire > 0) {
        return `In ${daysToExpire} days on ${DateTime.now()
          .plus({ days: daysToExpire })
          .toFormat("D")}`;
      } else {
        return "";
      }
    },
    generateCredentialApi: generateUserDataAccessCredential,
    disableCredentialApi: disableIndividualCredentialById,
    toasterMessage: "You successfully downloaded your credential!",
    modalTitle: "Download New Credential?",
    modalContent: `Downloading a new individual credential will cause the old credential
      to expire and disconnect your data access until you upload your new credential.
      Do you still want to continue?`,
    disableModalTitle: "Disable Individual Credential?",
    disableModalDescription:
      "Disabling this credential will cause it to immediately expire. Do you still want to continue?",
    getFileName: (credJson) => {
      return credJson.client_email.split("@")[0];
    },
    getPermissions: ({ userId, role, formEntity: profileUser }) => {
      const isProfileCurrentUser = userId === profileUser?.userId;
      const isCurrentUserAdmin = [ROLES.orgAdmin, ROLES.systemAdmin].includes(
        role
      );
      const isCurrentUserViewer = role === ROLES.systemViewer;

      let permissions = [];

      if (isProfileCurrentUser) {
        permissions.push(CREDENTIAL_PERMISSIONS.CAN_DOWNLOAD);
        permissions.push(CREDENTIAL_PERMISSIONS.CAN_DISABLE);
        permissions.push(CREDENTIAL_PERMISSIONS.CAN_VIEW_META);
      }

      if (isProfileCurrentUser || isCurrentUserAdmin) {
        permissions.push(CREDENTIAL_PERMISSIONS.CAN_DISABLE);
        permissions.push(CREDENTIAL_PERMISSIONS.CAN_VIEW_META);
      }

      if (isCurrentUserViewer) {
        permissions.push(CREDENTIAL_PERMISSIONS.CAN_VIEW_META);
      }

      return permissions;
    },
  },
  org: {
    text: `Download your organizations credential for BQ.
    This credential does not expire.`,
    disableModalTitle: "Disable Organization Credential?",
    disableModalDescription:
      "Disabling this organization's credential will cause is to immediately expire. Do you still want to continue?",
    getDownloadInfo: (generatedTimeStamp, creatorEmail, organization) => {
      const creator = organization.members.find(
        (member) => member.email === creatorEmail
      );
      const creatorLink = creator?.email
        ? `/organization/${creator.organization_id}/members/${creator.userId}`
        : "";

      if (creatorLink) {
        return (
          <span>
            <ButtonLink variant="text" to={creatorLink}>
              {creator.name || creator.email}
            </ButtonLink>{" "}
            on {DateTime.fromMillis(generatedTimeStamp).toFormat("D")}
          </span>
        );
      }

      return generatedTimeStamp
        ? `Credential downloaded on ${DateTime.fromMillis(
            generatedTimeStamp
          ).toFormat("D")}`
        : "";
    },
    getExpiryInfo: () => {
      return "";
    },
    generateCredentialApi: generateOrgDataAccessCredential,
    disableCredentialApi: disableOrganizationCredentialById,
    toasterMessage: "You successfully downloaded your credential!",
    modalTitle: "Download New Credential?",
    modalContent: `Downloading a new organization credential will cause the old credential to expire
      and disconnect your data access until you upload your new credential.`,
    getFileName: (credJson) => {
      return credJson.client_email.split("@")[0];
    },
    getPermissions: ({ role, organizationId, formEntity: organization }) => {
      const isCurrentUserOrgMember = organizationId === organization.id;
      const isCurrentUserAdmin = [ROLES.orgAdmin, ROLES.systemAdmin].includes(
        role
      );
      const isCurrentUserPopuliAdmin = role === ROLES.systemAdmin;
      const isCurrentUserViewer = role === ROLES.systemViewer;

      let permissions = [];

      if (isCurrentUserOrgMember && isCurrentUserAdmin) {
        permissions.push(CREDENTIAL_PERMISSIONS.CAN_DOWNLOAD);
        permissions.push(CREDENTIAL_PERMISSIONS.CAN_DISABLE);
        permissions.push(CREDENTIAL_PERMISSIONS.CAN_VIEW_META);
      }

      if (!isCurrentUserOrgMember && isCurrentUserPopuliAdmin) {
        permissions.push(CREDENTIAL_PERMISSIONS.CAN_DISABLE);
        permissions.push(CREDENTIAL_PERMISSIONS.CAN_VIEW_META);
      }

      if (isCurrentUserViewer) {
        permissions.push(CREDENTIAL_PERMISSIONS.CAN_VIEW_META);
      }

      return permissions;
    },
  },
};

export const CredentialNavSection = ({
  type,
  entity,
  isLoading,
  onMutationSuccess,
}) => {
  const { currentUser } = useCurrentUser();
  const { activeMembership } = useActiveMembership();

  const params = useParams();
  const { userId, orgId: organizationId } = params;

  const {
    text,
    getDownloadInfo,
    getExpiryInfo,
    generateCredentialApi,
    disableCredentialApi,
    getFileName,
    toasterMessage,
    modalTitle,
    modalContent,
    getPermissions,
    disableModalTitle,
    disableModalDescription,
  } = credSettings[type];

  const permissions = getPermissions({
    userId: currentUser.userId,
    role: activeMembership.role,
    organizationId: activeMembership.id,
    formEntity: entity,
  });

  const [downloadingOrgId, setDownloadingOrgId] = useState(null);
  const [showDownloadConfirmModal, setShowDownloadConfirmModal] =
    useState(false);
  const [disablingOrgId, setDisablingOrgId] = useState(null);
  const [showDisableConfirmModal, setShowDisableConfirmModal] = useState(false);
  const [error, setError] = useState(null);

  const generateCredential = useAuthenticatedMutation(generateCredentialApi);
  const disableCredential = useAuthenticatedMutation(disableCredentialApi);

  const [selectedOrgAction, setSelectedOrgAction] = useState(null);

  const handleDownloadClick = async ({
    organizationId,
    hasActiveCredential,
  }) => {
    if (hasActiveCredential) {
      setSelectedOrgAction({ organizationId });
      setShowDownloadConfirmModal(true);
    } else {
      setError(null);
      setDownloadingOrgId(organizationId);

      try {
        const result = await generateCredential({
          organizationId,
          userId,
        });
        const credential = result.credential;
        const fileName = getFileName(credential);
        downloadJson(credential, `credential-${fileName}`);

        // Notify
        toast.success(toasterMessage);

        // Bubble up successful action
        onMutationSuccess && onMutationSuccess();
      } catch (error) {
        console.error(error);
        setError(error);
      }

      setDownloadingOrgId(null);
    }
  };

  const handleDisableClick = async ({
    organizationId,
    activeCredentialInfo,
  }) => {
    setSelectedOrgAction({
      organizationId,
      activeCredentialInfo,
    });
    setShowDisableConfirmModal(true);
  };

  const handleDisableConfirm = async () => {
    if (!selectedOrgAction?.activeCredentialInfo) return;

    setError(null);
    setDisablingOrgId(selectedOrgAction.organizationId);

    try {
      await disableCredential({
        userId,
        organizationId: selectedOrgAction.organizationId,
        credentialId: selectedOrgAction.activeCredentialInfo.credential_id,
      });

      // Notify
      toast.success("Credential successfully disabled!");

      // Bubble up successful action
      onMutationSuccess && onMutationSuccess();
    } catch (error) {
      console.error(error);
      setError(error);
    }

    setDisablingOrgId(null);
    setShowDisableConfirmModal(false);
    setSelectedOrgAction(null);
  };

  const [openItems, setOpenItems] = useState([activeMembership.id]);

  useEffect(() => {
    // This will keep the accordion open for the current organization from among the memberships list
    setOpenItems([activeMembership.id]);
  }, [activeMembership.id]);

  const getCredentialMetaForOrg = (organizationId) => {
    const orgAccessInfo = entity.memberships?.find(
      (info) => info.organization_id === organizationId
    ).data_access_info;

    const activeCredentialInfo = getLastActiveCredential(orgAccessInfo);
    const hasActiveCredential = !!activeCredentialInfo;

    return {
      activeCredentialInfo,
      hasActiveCredential,
      downloadDate: getDownloadInfo(
        activeCredentialInfo?.generated_timestamp,
        activeCredentialInfo?.created_by,
        entity
      ),
      expiration: getExpiryInfo(
        activeCredentialInfo?.days_to_expire,
        activeCredentialInfo?.generated_timestamp
      ),
    };
  };

  return (
    <div style={{ maxWidth: "600px" }}>
      {/* Form Description */}
      {permissions.includes(CREDENTIAL_PERMISSIONS.CAN_DOWNLOAD) && (
        <Typography
          variant="p"
          noMargin
          color="var(--color-base-content)"
          style={{ lineHeight: "22px", marginBottom: "24px" }}
        >
          {text}
        </Typography>
      )}

      {/* Form Error */}
      <Error>{error}</Error>

      {type === "org" ? (
        (() => {
          const orgCredentialInfo = entity.data_access_info || {};
          const activeCredentialInfo =
            getLastActiveCredential(orgCredentialInfo);
          const hasActiveCredential = !!activeCredentialInfo;

          return (
            <div>
              {/* Credential metadata */}
              {permissions.includes(CREDENTIAL_PERMISSIONS.CAN_VIEW_META) && (
                <DetailsViewer
                  isLoading={isLoading}
                  style={{
                    flexDirection: "row",
                    paddingTop: "0px",
                    gap: "150px",
                  }}
                  titleStyle={{ fontSize: "12px" }}
                  itemStyle={{ gap: "4px" }}
                  contentStyle={{ color: "var(--text-header)" }}
                  data={[
                    {
                      label: "Downloaded By",
                      value: getDownloadInfo(
                        activeCredentialInfo?.generated_timestamp,
                        activeCredentialInfo?.created_by,
                        entity
                      ),
                    },
                  ]}
                />
              )}

              {/* Action buttons */}
              <Row
                style={{
                  gap: "8px",
                  justifyContent: "flex-end",
                  marginTop: "16px",
                }}
              >
                {hasActiveCredential &&
                  permissions.includes(CREDENTIAL_PERMISSIONS.CAN_DISABLE) && (
                    <Button
                      variant="outlined"
                      isLoading={disablingOrgId === entity.organization_id}
                      onClick={() =>
                        handleDisableClick({
                          organizationId: entity.id,
                          activeCredentialInfo,
                        })
                      }
                    >
                      Disable Current
                    </Button>
                  )}

                {permissions.includes(CREDENTIAL_PERMISSIONS.CAN_DOWNLOAD) && (
                  <Button
                    type="submit"
                    isLoading={downloadingOrgId === entity.organization_id}
                    onClick={() =>
                      handleDownloadClick({
                        organizationId: entity.id,
                        hasActiveCredential,
                      })
                    }
                  >
                    Download Credential
                  </Button>
                )}
              </Row>
            </div>
          );
        })()
      ) : (
        <Accordion openItems={openItems} setOpenItems={setOpenItems}>
          {currentUser.memberships.map((membership) => {
            const {
              activeCredentialInfo,
              hasActiveCredential,
              downloadDate,
              expiration,
            } = getCredentialMetaForOrg(membership.organization_id);

            return (
              <AccordionItem
                key={membership.organization_id}
                value={membership.organization_id}
                title={membership.organization_name}
                itemStyle={{
                  backgroundColor: "#FFFFFF",
                  border: `1px solid ${dh_colors.grey_60}`,
                  marginBottom: "24px",
                  padding: "16px 16px 0 16px",
                }}
                HeaderRight={() => {
                  return (
                    membership.organization_id === activeMembership.id && (
                      <Pill text="Current" size="small" variant="success" />
                    )
                  );
                }}
              >
                <AccordionContent>
                  <div style={{ paddingBottom: "16px" }}>
                    {/* Form Metadata */}
                    {permissions.includes(
                      CREDENTIAL_PERMISSIONS.CAN_VIEW_META
                    ) && membership.direct_data_access_enabled ? (
                      <DetailsViewer
                        isLoading={isLoading}
                        style={{
                          flexDirection: "row",
                          paddingTop: "0px",
                          gap: "150px",
                        }}
                        titleStyle={{ fontSize: "12px" }}
                        itemStyle={{ gap: "4px" }}
                        contentStyle={{ color: "var(--text-header)" }}
                        data={
                          type === "user"
                            ? // && activeCredentialInfo
                              [
                                {
                                  label: "Downloaded On",
                                  value: downloadDate,
                                },
                                {
                                  label: "Expiration",
                                  value: expiration,
                                },
                              ]
                            : [{ label: "Downloaded By", value: downloadDate }]
                        }
                      />
                    ) : (
                      <Typography
                        variant="p"
                        noMargin
                        color="var(--text-header)"
                        style={{ lineHeight: "22px" }}
                      >
                        Data Access has not been enabled for this organization.
                        Please contact your Org Admin to request access.
                      </Typography>
                    )}

                    {/* Form Actions */}
                    <Row
                      style={{
                        gap: "8px",
                        justifyContent: "flex-end",
                        marginTop: "16px",
                      }}
                    >
                      {membership.direct_data_access_enabled &&
                        hasActiveCredential &&
                        permissions.includes(
                          CREDENTIAL_PERMISSIONS.CAN_DISABLE
                        ) && (
                          <Button
                            variant="outlined"
                            isLoading={
                              disablingOrgId === membership.organization_id
                            }
                            onClick={() =>
                              handleDisableClick({
                                organizationId: membership.organization_id,
                                activeCredentialInfo,
                              })
                            }
                          >
                            Disable Current
                          </Button>
                        )}

                      {membership.direct_data_access_enabled &&
                        permissions.includes(
                          CREDENTIAL_PERMISSIONS.CAN_DOWNLOAD
                        ) && (
                          <Button
                            type="submit"
                            isLoading={
                              downloadingOrgId === membership.organization_id
                            }
                            onClick={() =>
                              handleDownloadClick({
                                organizationId: membership.organization_id,
                                hasActiveCredential,
                              })
                            }
                          >
                            Download Credential
                          </Button>
                        )}
                    </Row>
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      {/* Modal to confirm if new credential is needed while one is already genrated and active*/}
      {showDownloadConfirmModal ? (
        <DownloadNewCredentialModal
          title={modalTitle}
          content={modalContent}
          close={() => setShowDownloadConfirmModal(false)}
          credSettings={{
            generateCredential,
            toasterMessage,
            getFileName,
            onMutationSuccess,
          }}
        />
      ) : null}

      <ConfirmModal
        dataTest="disable-credential-modal"
        visible={showDisableConfirmModal}
        onNo={() => setShowDisableConfirmModal(false)}
        dismiss={() => setShowDisableConfirmModal(false)}
        noLabel="Cancel"
        onYes={handleDisableConfirm}
        isSubmitting={!!disablingOrgId}
        title={disableModalTitle}
      >
        <Error>{error}</Error>
        <Typography variant="p" style={{ maxWidth: "500px" }}>
          {disableModalDescription}
        </Typography>
      </ConfirmModal>
    </div>
  );
};
